import { Injectable, inject } from '@angular/core';
import {
  AuthApiService,
  decodeToken,
  isTokenExpired,
  toAuthenticatedUser,
} from '@saexpeditions/sdk/authentication';
import { LocalStorageService, SDK_CACHE_KEYS } from '@saexpeditions/sdk/data';
import { UserStateService } from '@core/state';

/**
 * Servicio de sesión basado en tokens de la API REST.
 * Reemplaza al viejo AuthSessionService (Firebase-based).
 *
 * Responsabilidades:
 * - Inicializar la sesión al cargar la app (provideAppInitializer)
 * - Establecer el estado del usuario desde un token (post-login)
 * - Limpiar la sesión (logout)
 */
@Injectable({
  providedIn: 'root',
})
export class TokenSessionService {
  private readonly storage = inject(LocalStorageService);
  private readonly authApi = inject(AuthApiService);
  private readonly userState = inject(UserStateService);

  /**
   * Inicializa la sesión del usuario al cargar la aplicación.
   * Debe llamarse desde provideAppInitializer.
   *
   * Flujo:
   * 1. Lee el token de localStorage
   * 2. Si existe y es válido → decodifica y popula el estado
   * 3. Si existe pero expiró → intenta refresh
   * 4. Si no hay token o el refresh falla → estado = unauthenticated
   */
  async initialize(): Promise<void> {
    const token = this.storage.get<string>(SDK_CACHE_KEYS.USER_TOKEN);

    if (!token) {
      this.userState.setState({ user: null, authStatus: 'unauthenticated' });
      return;
    }

    // Token exists and is valid
    if (!isTokenExpired(token)) {
      this.setSessionFromToken(token);
      return;
    }

    // Token expired → try refresh
    try {
      const tokens = await this.authApi.refreshToken();
      this.setSessionFromToken(tokens.idToken);
    } catch {
      this.userState.setState({ user: null, authStatus: 'unauthenticated' });
    }
  }

  /**
   * Decodifica un idToken y establece el estado del usuario como autenticado.
   * Llamado después del login y después de un refresh exitoso.
   *
   * @param idToken - JWT enriquecido con claims del usuario
   */
  setSessionFromToken(idToken: string): void {
    const payload = decodeToken(idToken);

    if (!payload) {
      this.userState.setState({ user: null, authStatus: 'unauthenticated' });
      return;
    }

    const user = toAuthenticatedUser(payload);
    this.userState.setState({ user, authStatus: 'authenticated' });
  }

  /**
   * Limpia la sesión: elimina tokens y resetea el estado del usuario.
   */
  clearSession(): void {
    this.authApi.clearTokens();
    this.userState.setState({ user: null, authStatus: 'unauthenticated' });
  }
}
